import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {AppLayoutFacadeService} from '../app-layout-facade.service';
import {combineLatest, Observable, Subscription} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {School} from '@model/school';
import {DateTime} from 'luxon';
import {AgendaActivity} from '@model/agenda-activity';
import {PublicMessage} from '@model/public-message';
import {DATE_API_FORMAT} from '@shared/util/date-util';
import {MatDividerModule} from '@angular/material/divider';
import {MatChipsModule} from '@angular/material/chips';
import {PublicMessageComponent} from './components/public-message/public-message.component';
import {AsyncPipe} from '@angular/common';
import {SchoolYearComponent} from "@shared/school-year/school-year.component";
import {ParentMeetingSlot} from '@model/parent-meeting-slot';
import {ParentMeetingSlotListComponent} from 'src/app/parent/parent-meetings/components/parent-meeting-slot-list/parent-meeting-slot-list.component';
import {ElementarySchoolShortcutsComponent} from './components/elementary-school-shortcuts/elementary-school-shortcuts.component';
import {SecondarySchoolShortcutsComponent} from './components/secondary-school-shortcuts/secondary-school-shortcuts.component';
import {LiteSchoolShortcutsComponent} from './components/lite-school-shortcuts/lite-school-shortcuts.component';

const MATERIAL_IMPORTS = [
  MatChipsModule,
  MatDividerModule,
];

@Component({
    selector: 'app-app-welcome',
    templateUrl: './app-welcome.component.html',
    styleUrls: ['./app-welcome.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
      ...MATERIAL_IMPORTS,
      AsyncPipe, 
      PublicMessageComponent, 
      SchoolYearComponent, 
      ParentMeetingSlotListComponent, 
      ElementarySchoolShortcutsComponent, 
      SecondarySchoolShortcutsComponent,
      LiteSchoolShortcutsComponent
    ]
})
export class AppWelcomeComponent implements OnInit, OnDestroy {

  school$: Observable<School>;
  schoolName$: Observable<string>;
  isSuperAdmin: boolean;
  isTeacherStaffMember$: Observable<boolean>;
  isEducatorStaffMember$: Observable<boolean>;
  isTeacherOrEducatorStaffMember$: Observable<boolean>;
  isAdminStaffMember$: Observable<boolean>;
  isParent$: Observable<boolean>;
  isStaffMember$: Observable<boolean>;
  firstName$: Observable<string>;
  lastName$: Observable<string>;
  activeSchoolYear$: Observable<number>;
  parentMeetingSlots$: Observable<ParentMeetingSlot[]>;
  isElementarySchool$: Observable<boolean>;
  isCabangaLite$: Observable<boolean>;
  
  isMobile$: Observable<boolean>;
  businessDays: DateTime[];
  agendaActivities$: Observable<AgendaActivity[]>;
  publicMessage$: Observable<PublicMessage>;
  subscription: Subscription;

  constructor(private _appFacade: AppLayoutFacadeService) { }

  ngOnInit() {
    this.school$ = this._appFacade.school$;
    this.schoolName$ = this._appFacade.schoolName$;
    this.firstName$ = this._appFacade.firstName$;
    this.lastName$ = this._appFacade.lastName$;
    this.isSuperAdmin = this._appFacade.isSuperAdmin;
    this.isTeacherStaffMember$ = this._appFacade.isTeacherStaffMember$;
    this.isEducatorStaffMember$ = this._appFacade.isEducatorStaffMember$;
    this.isStaffMember$ = this._appFacade.isStaffMember$;
    this.isAdminStaffMember$ = this._appFacade.isAdminStaffMember$;
    this.isTeacherOrEducatorStaffMember$ = combineLatest([this.isTeacherStaffMember$, this.isEducatorStaffMember$]).pipe(
      map(([isTeacher, isEducator]) => isTeacher || isEducator)
    );
    this.isParent$ = this._appFacade.isParent$;
    this.activeSchoolYear$ = this._appFacade.school$.pipe(
      map( school => school.activeSchoolYear)
    );
    this.isElementarySchool$ = this._appFacade.school$.pipe(
      map(school => school.elementarySchool)
    );
    this.isCabangaLite$ = this._appFacade.school$.pipe(
      map(school => school.cabangaLite)
    );
    const today = DateTime.now();
    this.isMobile$ = this._appFacade.isMobile$;
    this.isMobile$.subscribe(isMobile => {
      this.businessDays = this.generateBusinessDays(today, isMobile ? 5 : 5);
    });
    this._appFacade.loadAgendaActivities();
    this.agendaActivities$ = this._appFacade.agendaActivities$;
    this._appFacade.loadPublicMessage();
    this.publicMessage$ = this._appFacade.publicMessage$;
    this.subscription = combineLatest([this.isParent$, this.isCabangaLite$]).pipe(
      filter(([isParent, isCabangaLite]) => isParent && !isCabangaLite)
    ).subscribe(() => this._appFacade.loadParentMeetingSlots());
    this.parentMeetingSlots$ = this._appFacade.parentMeetingSlots$;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this._appFacade.parentMeetingSlotsReset();
  }

  private generateBusinessDays(originalDate: DateTime, numDaysToGenerate: number): DateTime[] {
    let daysRemaining = numDaysToGenerate;
    let currentDate = originalDate.minus({days: 1});
    const businessDays = [];
    while (daysRemaining > 0) {
      currentDate = currentDate.plus({days: 1});
      if (currentDate.weekday !== 6 && currentDate.weekday !== 7) {
        daysRemaining--;
        businessDays.push(currentDate);
      }
    }
    return businessDays;
  }

  getDay(day: DateTime): string {
    return day.toFormat('dd');
  }

  getMonth(day: DateTime): string {
    return day.setLocale('fr').toFormat('MMM');
  }

  getActivities$(day: DateTime): Observable<AgendaActivity[]> {
    return this.agendaActivities$.pipe(
      map(activities => activities.filter(
        activity => activity.date.toFormat(DATE_API_FORMAT) === day.toFormat(DATE_API_FORMAT)))
    );
  }

  savePublicMessage(publicMessage: PublicMessage) {
    this._appFacade.savePublicMessage(publicMessage);
  }
}
